import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, PLATFORM_ID, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import type { ApiWrapped } from '@core/http/api-response.types';
import { apiUrl } from '@core/http/api-url.util';
import { setStoredAuthToken } from '@core/auth/auth-token.storage';

@Component({
  selector: 'app-admin-login-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './admin-login-page.component.html',
  styleUrl: './admin-login-page.component.css',
  host: { ngSkipHydration: 'true' },
})
export class AdminLoginPageComponent {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly platformId = inject(PLATFORM_ID);

  readonly email = signal('');
  readonly password = signal('');
  readonly showPassword = signal(false);
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);

  togglePassword(): void {
    this.showPassword.update((v) => !v);
  }

  submit(): void {
    if (!isPlatformBrowser(this.platformId) || this.busy()) return;
    const email = this.email().trim();
    if (!email || !this.password()) return;

    this.busy.set(true);
    this.error.set(null);

    this.http
      .post<ApiWrapped<{ accessToken: string }>>(apiUrl('/auth/admin/login'), {
        email,
        password: this.password(),
      })
      .subscribe({
        next: async (res) => {
          const token = res.data?.accessToken;
          if (!token) {
            this.error.set('Login failed');
            this.busy.set(false);
            return;
          }
          setStoredAuthToken(token);
          const next = this.route.snapshot.queryParamMap.get('next');
          const target =
            next && next.startsWith('/admin') && !next.startsWith('//')
              ? next
              : '/admin';
          await this.router.navigateByUrl(target);
          this.busy.set(false);
        },
        error: (e: { error?: { error?: { message?: string } } }) => {
          this.error.set(
            e?.error?.error?.message ?? 'Invalid admin credentials',
          );
          this.busy.set(false);
        },
      });
  }
}
